var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _a;
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { domUtils, environmentUtils } from "conversional-utils";
import get from 'lodash/get';
// For retina displays, the pixel ratio is 2 since each pixel is 2x2 pixels in real
var WINDOW_DEVICE_PIXEL_RATIO = get(window, 'devicePixelRatio', 1);
var PIXEL_RATIO = isNaN(WINDOW_DEVICE_PIXEL_RATIO) ? 1 : WINDOW_DEVICE_PIXEL_RATIO;
// Recalculation values
var MAX_RECALCULATION_ITERATIONS = 4;
var RECALCULATION_INTERVAL = 7000;
// Dividing default width to pixel ratio because finally we are multiplying the width with pixel ratio
var DEFAULT_WIDTH = 600 / PIXEL_RATIO;
var BREAKPOINTS = (_a = {
        100: 'xs',
        200: 'sm',
        400: 'md',
        600: 'lg',
        900: 'xl'
    },
    _a[Infinity] = 'xxl',
    _a);
var ImageWrapper = function (_a) {
    var src = _a.src, _b = _a.breakpoints, breakpoints = _b === void 0 ? BREAKPOINTS : _b, getWidth = _a.getWidth, setImageRef = _a.setImageRef, onLoad = _a.onLoad, processWidth = _a.processWidth, _c = _a.srcType, srcType = _c === void 0 ? "h" : _c, aspectRatio = _a.aspectRatio, restProps = __rest(_a, ["src", "breakpoints", "getWidth", "setImageRef", "onLoad", "processWidth", "srcType", "aspectRatio"]);
    var SIZES = Object.keys(breakpoints);
    var _d = useState(null), width = _d[0], setWidth = _d[1];
    var imageRef = useRef(null);
    var handleImageLoad = function (img) { return onLoad && onLoad(img); };
    var imageOnLoadListener = function (ev) { return handleImageLoad(ev.target); };
    /**
     * This useEffect is responsible for managing the image loading process.
     *
     * - It optionally stores the `imageRef` in the parent component via the `setImageRef` callback, if provided. We may need to manage the ref in the parent.
     * - It retrieves the image DOM element from the `imageRef` and checks if the image has already completed loading:
     *      - If the image is already fully loaded (e.g., cached by the browser), it immediately triggers the `handleImageLoad` function.
     *      - If the image has not yet loaded, it attaches an event listener to the 'load' event, which will call `imageOnLoadListener` once the image finishes loading.
     * - When the component unmounts or dependencies change, the event listener is removed to prevent memory leaks or accidental multiple calls.
     */
    useEffect(function () {
        setImageRef && setImageRef(imageRef);
        var img = imageRef.current;
        if (img)
            (img.complete && img.naturalWidth) ? handleImageLoad(img) : img.addEventListener('load', imageOnLoadListener);
        return function () {
            img && img.removeEventListener('load', imageOnLoadListener);
        };
    }, []);
    var getImageWidth = function () { return domUtils.getComputedStylesWidth(imageRef.current); };
    var getParentWidth = function () { return imageRef.current.parentElement.clientWidth ||
        domUtils.getComputedStylesWidth(imageRef.current.parentElement); };
    function calculateWidth() {
        var width = function () {
            // We can force the width of the image from parent as a prop
            if (getWidth) {
                var propWidth = getWidth();
                if (propWidth)
                    return propWidth;
            }
            try {
                /**
                * For width calculations, we first try to get the width of the image element itself.
                * There, we assume the max/min width from the styles.
                * If it's not available, we try to get the width of the parent element.
                * */
                return getImageWidth() || getParentWidth();
            }
            catch (e) {
                return null;
            }
        }();
        /**
         * We can pass a function to process and manipulate calculated width of the element
        * */
        if (processWidth)
            return processWidth(width);
        return width;
    }
    function getUsableWidth(defaultWidth) {
        if (isNaN(width) || width === -1)
            return defaultWidth || DEFAULT_WIDTH;
        return width;
    }
    var getSelectedSrc = function () { return typeof src === "string" ? src : get(src, srcType, ""); };
    /**
     * width: -1 is for non-Conversional hosted images
     * The default resolution is 'md' for images that we cannot calculate their prerendered width
     * */
    function getSrc() {
        var imageSource = getSelectedSrc();
        if (width === -1)
            return imageSource;
        if (width === 0)
            return '';
        if (!width && !imageSource)
            return '';
        // Multiplying the width with pixel ratio to get the actual relevant width - For retina displays
        var calculatedWidth = (width || getUsableWidth()) * PIXEL_RATIO;
        return environmentUtils.generateMultiResolutionSrc({
            imageSource: imageSource,
            width: calculatedWidth,
            aspectRatio: aspectRatio
        });
    }
    useEffect(function () {
        var isConversionalHostedImage = environmentUtils.isImageInConversionalDomain(getSelectedSrc());
        setWidth(isConversionalHostedImage ? calculateWidth() : -1);
        // region Recalculate image size
        /**
        * Here we recalculate image width to make sure after all shrinking of the page
         *  and all loads, we've loaded the best quality of the image. This is usable both for resizes and for shallow preloading.
        * */
        var imageSizeCalculatorInterval = null;
        var iteration = 0;
        if (isConversionalHostedImage) {
            imageSizeCalculatorInterval = setInterval(function () {
                var newWidth = calculateWidth();
                if (newWidth > width) {
                    setWidth(newWidth);
                }
                if (iteration++ === MAX_RECALCULATION_ITERATIONS)
                    clearInterval(imageSizeCalculatorInterval);
            }, RECALCULATION_INTERVAL);
        }
        // endregion
        return function () { return clearInterval(imageSizeCalculatorInterval); };
    }, []);
    var imgSrc = getSrc();
    return _jsx("img", __assign({ src: imgSrc, ref: imageRef }, restProps));
};
export default ImageWrapper;
